import { useEffect, useRef, useState } from 'react';
import { useVersionStore } from '../stores/versionStore';
import { useDREStore } from '../stores/dreStore';

interface UseAutoSaveOptions {
  enabled?: boolean;
  debounceMs?: number;
  versionNamePrefix?: string;
}

interface AutoSaveStatus {
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
}

/**
 * Hook para auto-save com debounce do estado DRE
 * 
 * @param options - Configurações do auto-save
 * @returns Status do auto-save e função para forçar salvamento
 * 
 * @example
 * ```tsx
 * const { status, forceSave } = useAutoSave({
 *   enabled: true,
 *   debounceMs: 5000,
 *   versionNamePrefix: 'Auto-save'
 * });
 * 
 * return (
 *   <div>
 *     {status.isSaving && <span>Salvando...</span>}
 *     {status.hasUnsavedChanges && <span>Alterações não salvas</span>}
 *   </div>
 * );
 * ```
 */
export function useAutoSave(options: UseAutoSaveOptions = {}) {
  const {
    enabled = true,
    debounceMs = 5000, // 5 segundos por padrão
    versionNamePrefix = 'Auto-save'
  } = options;

  const saveVersion = useVersionStore(state => state.saveVersion);
  const dreData = useDREStore(state => state.dreData);
  
  const [status, setStatus] = useState<AutoSaveStatus>({
    isSaving: false,
    lastSaved: null,
    hasUnsavedChanges: false
  });

  // Ref para armazenar o último estado salvo (hash simples)
  const lastSavedHashRef = useRef<string>('');
  // Ref para o timer de debounce
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Ref para evitar race conditions
  const isSavingRef = useRef(false);

  // Função para calcular hash do estado DRE (simples stringify)
  const calculateHash = () => {
    if (!dreData) return '';
    return JSON.stringify({
      setima: dreData.setima,
      studio: dreData.studio555
    });
  };

  // Função para salvar versão
  const performSave = async () => {
    if (isSavingRef.current) return;
    
    try {
      isSavingRef.current = true;
      setStatus(prev => ({ ...prev, isSaving: true }));

      const now = new Date();
      const timestamp = now.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      await saveVersion(
        `${versionNamePrefix} ${timestamp}`,
        'Versão salva automaticamente pelo sistema'
      );

      const currentHash = calculateHash();
      lastSavedHashRef.current = currentHash;

      setStatus({
        isSaving: false,
        lastSaved: now,
        hasUnsavedChanges: false
      });
    } catch (error) {
      console.error('Erro ao salvar versão automaticamente:', error);
      setStatus(prev => ({ ...prev, isSaving: false }));
    } finally {
      isSavingRef.current = false;
    }
  };

  // Função para forçar salvamento imediato
  const forceSave = async () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    await performSave();
  };

  // Effect para detectar mudanças e iniciar debounce
  useEffect(() => {
    if (!enabled) return;

    const currentHash = calculateHash();
    
    // Se não há hash anterior, armazena o atual (primeira renderização)
    if (!lastSavedHashRef.current) {
      lastSavedHashRef.current = currentHash;
      return;
    }

    // Se o hash mudou, marca como tendo alterações não salvas
    if (currentHash !== lastSavedHashRef.current) {
      setStatus(prev => ({ ...prev, hasUnsavedChanges: true }));

      // Cancela timer anterior se existir
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Inicia novo timer de debounce
      debounceTimerRef.current = setTimeout(() => {
        performSave();
      }, debounceMs);
    }

    // Cleanup: cancela timer ao desmontar ou antes de novo effect
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [dreData, enabled, debounceMs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup final ao desmontar componente
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    status,
    forceSave
  };
}
